import type { StartupPolicySettings } from "@evolution/shared";
import { validStartupPolicy } from "./startup-policy-validation";
import { resolveApiEndpoint } from "../api/client";
import { Capacitor, registerPlugin } from "@capacitor/core";

type ContentStatus = "current" | "staged" | "offline" | "incompatible" | "deferred";
interface NativeContentPlugin {
  ready(options: { version: string }): Promise<void>;
  checkForUpdate(options: StartupPolicySettings): Promise<{ status: ContentStatus }>;
  setGameActive(options: { active: boolean }): Promise<void>;
}
const content = registerPlugin<NativeContentPlugin>("VarnaxContent");
let ready: Promise<boolean> | undefined;
let checking: Promise<ContentStatus> | undefined;
let lastCheck = 0;
let gameActive = false;
const CHECK_INTERVAL = 30 * 60 * 1000;

/** Includes entry/auth transitions, not just the already mounted arena. */
export function isNativeContentGameActive(): boolean { return gameActive; }

export function usesNativeContent(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android"
    && typeof window !== "undefined" && window.location.origin === "http://localhost";
}

/** Remove only Varnax's old browser caches; never clear account/preferences storage. */
export async function prepareNativeContent(): Promise<void> {
  if (!usesNativeContent()) return;
  // A bundled page without an active controller cannot be serving stale
  // service-worker assets. Avoid delaying every native cold start to scan
  // registrations and CacheStorage when there is nothing to clean up.
  if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) return;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    clearNativeBrowserCaches(),
    new Promise<void>((resolve) => { timeout = setTimeout(resolve, 1500); }),
  ]);
  if (timeout) clearTimeout(timeout);
}

async function clearNativeBrowserCaches(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.filter((registration) => {
        const worker = registration.active ?? registration.waiting ?? registration.installing;
        return worker && new URL(worker.scriptURL).origin === "http://localhost"
          && new URL(worker.scriptURL).pathname === "/sw.js";
      }).map((registration) => registration.unregister()));
    }
    if (typeof caches !== "undefined") {
      const names = await caches.keys();
      await Promise.all(names.filter((name) => name.startsWith("varnax-static-")).map((name) => caches.delete(name)));
    }
  } catch { /* Offline/restricted WebView must still use its bundled UI. */ }
}

/** Cancel downloads when a match starts; native never touches play/session storage. */
export async function setNativeContentGameActive(active: boolean): Promise<void> {
  gameActive = active;
  if (!usesNativeContent()) return;
  if (active) lastCheck = 0;
  try { await content.setGameActive({ active }); } catch { /* Older AAB has no updater. */ }
}

/** Call after the real App is usable, not from a loading screen or module import. */
export function markNativeContentReady(): Promise<boolean> {
  if (!usesNativeContent()) return Promise.resolve(false);
  ready ??= acknowledgeNativeContentReady();
  return ready;
}

async function acknowledgeNativeContentReady(): Promise<boolean> {
  // A transient bridge/storage rejection must not permanently cache a failed
  // health acknowledgement while the real App is already usable. Each retry
  // goes through the SAME native version/hash verification; no bypass and no
  // download/activation API. Bounded failures retain the native boot watchdog.
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      await content.ready({ version: import.meta.env.VITE_RELEASE_VERSION as string });
      return true;
    } catch {
      if (attempt < 2) await new Promise<void>((resolve) => setTimeout(resolve, attempt === 0 ? 250 : 1000));
    }
  }
  return false;
}

/** Menu-only check. Native code may STAGE, but has no JS-triggerable reload API. */
export function checkNativeContentUpdate(isInMatch: boolean): Promise<ContentStatus> {
  if (!usesNativeContent() || isInMatch || gameActive || !ready) return Promise.resolve("current");
  if (checking) return checking;
  const now = Date.now();
  if (lastCheck && now - lastCheck < CHECK_INTERVAL) return Promise.resolve("current");
  lastCheck = now;
  checking = ready.then(async (confirmed) => {
    if (!confirmed) return "incompatible" as const;
    if (gameActive) { lastCheck = 0; return "deferred" as const; }
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      let policy: { config?: unknown };
      try {
        const response = await fetch(resolveApiEndpoint("/api/startup-policy"), { credentials: "omit", cache: "no-store", signal: controller.signal });
        if (!response.ok) return "deferred" as const;
        policy = await response.json() as typeof policy;
      } finally { clearTimeout(timeout); }
      if (!validStartupPolicy(policy.config) || !policy.config.enabled || gameActive) return "deferred" as const;
      const result = (await content.checkForUpdate(policy.config)).status;
      if (result === "deferred") lastCheck = 0;
      return result;
    }
    catch { return "offline" as const; }
  }).finally(() => { checking = undefined; });
  return checking;
}
